import { HttpErrorResponse } from '@angular/common/http';
import { createFeature, createReducer, on } from '@ngrx/store';
import { DividendYield, DividendYieldTotal, NewsItem, PortfolioPerformance } from '@zero/shared/util-models';
import { ChartPeriodDates, DepotChartSerie, Stats } from '../entities/chart-and-stats.entity';
import { Dividend } from '../entities/dividends.entity';
import { MyTrades } from '../entities/my-trades.entity';
import { Filter, Trade } from '../entities/trade.entity';
import { Weighting, WeightingType } from '../entities/weighting.entity';
import { StatisticsActions, StatisticsApiActions } from './statistics.actions';

export const STATISTICS_FEATURE_KEY = 'statistics';

interface StatisticsState {
  error: HttpErrorResponse | null;
  trades: Trade[];
  tradeTickerLoading: boolean;
  tradeTickerInitiallyLoaded: boolean;
  tradeTickerFilter: Filter;
  myTradesLoading: boolean;
  myTrades: MyTrades;
  weightingByAssetType: Weighting;
  weightingByAssetTypeLoading: boolean;
  weightingByTopPositions: Weighting;
  weightingByTopPositionsLoading: boolean;
  weightingByPositions: Weighting;
  weightingByPositionsLoading: boolean;
  weightingType: WeightingType;
  news: NewsItem[];
  newsLoading: boolean;
  chartAndStatsLoading: boolean;
  chartAndStatsInitiallyLoaded: boolean;
  chartWithCash: DepotChartSerie[];
  chartWithoutCash: DepotChartSerie[];
  chartPeriodDates: ChartPeriodDates;
  stats: Stats;
  performance: PortfolioPerformance;
  dividends: Dividend[];
  dividendYields: DividendYield[];
  dividendYieldTotal: DividendYieldTotal;
  dividendYieldsLoading: boolean;
  dividendYieldsInitiallyLoaded: boolean;
}

const initialState: StatisticsState = {
  error: null,
  trades: null,
  tradeTickerLoading: false,
  tradeTickerInitiallyLoaded: false,
  tradeTickerFilter: null,
  myTradesLoading: false,
  myTrades: null,
  weightingByAssetType: null,
  weightingByAssetTypeLoading: false,
  weightingByTopPositions: null,
  weightingByTopPositionsLoading: false,
  weightingByPositions: null,
  weightingByPositionsLoading: false,
  weightingType: null,
  news: null,
  newsLoading: false,
  chartAndStatsLoading: false,
  chartAndStatsInitiallyLoaded: false,
  chartWithCash: null,
  chartWithoutCash: null,
  chartPeriodDates: null,
  stats: null,
  performance: null,
  dividends: null,
  dividendYields: null,
  dividendYieldTotal: null,
  dividendYieldsLoading: false,
  dividendYieldsInitiallyLoaded: false,
};

export const statisticsFeature = createFeature({
  name: STATISTICS_FEATURE_KEY,
  reducer: createReducer(
    initialState,
    on(StatisticsActions.loadTradeTicker, (state): StatisticsState => ({ ...state, error: null, tradeTickerLoading: true })),
    on(
      StatisticsApiActions.loadTradeTickerSuccess,
      (state, { trades }): StatisticsState => ({
        ...state,
        trades,
        tradeTickerLoading: false,
        tradeTickerInitiallyLoaded: true,
      }),
    ),
    on(
      StatisticsApiActions.loadTradeTickerFailure,
      (state, { error }): StatisticsState => ({ ...state, error, tradeTickerLoading: false }),
    ),
    on(StatisticsActions.setTradeTickerLoading, (state, { tradeTickerLoading }): StatisticsState => ({ ...state, tradeTickerLoading })),
    on(
      StatisticsActions.toggleTradeTickerFilter,
      (state, { tradeTickerFilter }): StatisticsState => ({
        ...state,
        tradeTickerFilter,
      }),
    ),
    on(
      StatisticsActions.pushTradeTick,
      (state, { trade }): StatisticsState => ({
        ...state,
        trades: [trade, ...state.trades],
      }),
    ),
    on(StatisticsActions.loadMyTrades, (state): StatisticsState => ({ ...state, error: null, myTradesLoading: true })),
    on(
      StatisticsApiActions.loadMyTradesSuccess,
      (state, { myTrades }): StatisticsState => ({
        ...state,
        myTrades,
        myTradesLoading: false,
      }),
    ),
    on(StatisticsApiActions.loadTradeTickerFailure, (state, { error }): StatisticsState => ({ ...state, error, myTradesLoading: false })),
    on(
      StatisticsActions.loadWeightingByAssetType,
      (state): StatisticsState => ({
        ...state,
        error: null,
        weightingByAssetTypeLoading: true,
        weightingType: WeightingType.ASSETS,
      }),
    ),
    on(
      StatisticsApiActions.loadWeightingByAssetTypeSuccess,
      (state, { weightingByAssetType }): StatisticsState => ({
        ...state,
        weightingByAssetType,
        weightingByAssetTypeLoading: false,
      }),
    ),
    on(
      StatisticsApiActions.loadWeightingByAssetTypeFailure,
      (state, { error }): StatisticsState => ({
        ...state,
        error,
        weightingByAssetTypeLoading: false,
      }),
    ),

    on(
      StatisticsActions.loadWeightingByTopPositions,
      (state): StatisticsState => ({
        ...state,
        error: null,
        weightingByTopPositionsLoading: true,
        weightingType: WeightingType.TOP_POSITIONS,
      }),
    ),
    on(
      StatisticsApiActions.loadWeightingByTopPositionsSuccess,
      (state, { weightingByTopPositions }): StatisticsState => ({
        ...state,
        weightingByTopPositions,
        weightingByTopPositionsLoading: false,
      }),
    ),
    on(
      StatisticsApiActions.loadWeightingByTopPositionsFailure,
      (state, { error }): StatisticsState => ({
        ...state,
        error,
        weightingByTopPositionsLoading: false,
      }),
    ),
    on(
      StatisticsActions.loadWeightingByPositions,
      (state): StatisticsState => ({
        ...state,
        error: null,
        weightingByTopPositionsLoading: true,
        weightingType: WeightingType.ALL_POSITIONS,
      }),
    ),
    on(
      StatisticsApiActions.loadWeightingByPositionsSuccess,
      (state, { weightingByPositions }): StatisticsState => ({
        ...state,
        weightingByPositions,
        weightingByPositionsLoading: false,
      }),
    ),
    on(
      StatisticsApiActions.loadWeightingByPositionsFailure,
      (state, { error }): StatisticsState => ({
        ...state,
        error,
        weightingByPositionsLoading: false,
      }),
    ),
    on(StatisticsActions.loadMyNews, (state): StatisticsState => ({ ...state, error: null })),
    on(StatisticsActions.setNewsLoading, (state): StatisticsState => ({ ...state, newsLoading: true })),
    on(
      StatisticsApiActions.loadMyNewsSuccess,
      (state, { news }): StatisticsState => ({
        ...state,
        news,
        newsLoading: false,
      }),
    ),
    on(
      StatisticsApiActions.loadMyNewsFailure,
      (state, { error }): StatisticsState => ({
        ...state,
        error,
        newsLoading: false,
      }),
    ),
    on(StatisticsActions.loadChartAndStats, (state): StatisticsState => ({ ...state, error: null })),
    on(StatisticsActions.setChartsAndStatsLoading, (state, { loading }): StatisticsState => ({ ...state, chartAndStatsLoading: loading })),
    on(
      StatisticsApiActions.loadChartAndStatsSuccess,
      (state, { chartAndStats }): StatisticsState => ({
        ...state,
        chartWithCash: chartAndStats ? chartAndStats.withCashSeries : [],
        chartWithoutCash: chartAndStats ? chartAndStats.withoutCashSeries : [],
        stats: chartAndStats ? chartAndStats.stats : null,
        chartAndStatsLoading: false,
        chartAndStatsInitiallyLoaded: true,
      }),
    ),
    on(
      StatisticsApiActions.loadChartAndStatsFailure,
      (state, { error }): StatisticsState => ({
        ...state,
        error,
        chartAndStatsLoading: false,
      }),
    ),
    on(
      StatisticsActions.changeChartPeriod,
      (state, { from, to }): StatisticsState => ({
        ...state,
        chartPeriodDates: { from, to },
      }),
    ),
    on(
      StatisticsActions.pushPerformance,
      (state, { performance }): StatisticsState => ({
        ...state,
        performance,
      }),
    ),
    on(
      StatisticsApiActions.loadDividendsSuccess,
      (state, { dividends }): StatisticsState => ({
        ...state,
        dividends,
      }),
    ),
    on(
      StatisticsApiActions.loadChartAndStatsFailure,
      (state, { error }): StatisticsState => ({
        ...state,
        error,
        dividends: null,
      }),
    ),
    on(
      StatisticsActions.loadDividendYields,
      (state): StatisticsState => ({ ...state, dividendYieldsLoading: !state.dividendYieldsInitiallyLoaded }),
    ),
    on(
      StatisticsApiActions.loadDividendYieldsSuccess,
      (state, { dividendYields }): StatisticsState => ({
        ...state,
        dividendYields,
        dividendYieldsInitiallyLoaded: true,
        dividendYieldsLoading: false,
      }),
    ),
    on(
      StatisticsApiActions.loadDividendYieldsFailure,
      (state): StatisticsState => ({
        ...state,
        dividendYieldsLoading: false,
      }),
    ),
    on(
      StatisticsApiActions.loadDividendYieldTotalByPositionSuccess,
      (state, { dividendYieldTotal }): StatisticsState => ({
        ...state,
        dividendYieldTotal,
      }),
    ),
    on(
      StatisticsApiActions.loadDividendYieldTotalByPositionFailure,
      (state, { error }): StatisticsState => ({
        ...state,
        error,
        dividendYieldTotal: null,
      }),
    ),
    on(
      StatisticsActions.clearDividendYieldTotalByPosition,
      (state): StatisticsState => ({
        ...state,
        dividendYieldTotal: null,
      }),
    ),
  ),
});
